import { Node, mergeAttributes } from '@tiptap/core';

export const Callout = Node.create({
  name: 'callout',
  group: 'block',
  content: 'inline*',
  defining: true,

  addOptions() {
    return {
      templates: null,
      fetchRenderedHtml: null,     // async (attrs) => string | null — injected by controller
      onRenderedHtmlFetched: null, // () => void — called after cache is updated
    };
  },

  addStorage() {
    return {
      renderedHtmlCache: new Map(), // keyed by calloutStyle string
    };
  },

  onCreate() {
    // Seed cache from init-time templates so renderHTML works before any lazy fetches complete
    const initTemplates = this.options.templates?.rendered_html;
    if (initTemplates) {
      for (const [style, html] of Object.entries(initTemplates)) {
        this.storage.renderedHtmlCache.set(style, html);
      }
    }
  },

  addAttributes() {
    return {
      calloutStyle: {
        default: 'info',
        parseHTML: element => element.getAttribute('data-callout-style') || 'info',
        renderHTML: attrs => ({ 'data-callout-style': attrs.calloutStyle }),
      },
    };
  },

  parseHTML() {
    return [{
      tag: 'div[data-callout]',
      contentElement: 'span[data-callout-content]',
    }];
  },

  renderHTML({ HTMLAttributes }) {
    const calloutStyle = HTMLAttributes['data-callout-style'] || 'info';
    const cachedHtml = this.storage.renderedHtmlCache.get(calloutStyle);

    if (cachedHtml) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(cachedHtml.trim(), 'text/html');
      const dom = doc.body.firstChild.cloneNode(true);
      const contentDOM = dom.querySelector('[data-callout-content]');
      Object.entries(HTMLAttributes).forEach(([k, v]) => dom.setAttribute(k, v));
      return { dom, contentDOM };
    }

    return [
      'div',
      mergeAttributes({ 'data-callout': '' }, HTMLAttributes),
      ['span', { 'data-callout-content': '' }, 0],
    ];
  },

  addNodeView() {
    return ({ node, getPos, editor }) => {
      const extension = this;

      const fetchAndCache = (attrs) => {
        if (!extension.options.fetchRenderedHtml) return;
        const cacheKey = attrs.calloutStyle;
        if (extension.storage.renderedHtmlCache.has(cacheKey)) return;
        extension.options.fetchRenderedHtml(attrs).then(html => {
          if (html) {
            extension.storage.renderedHtmlCache.set(cacheKey, html);
            extension.options.onRenderedHtmlFetched?.();
          }
        });
      };

      const dom = document.createElement('div');
      dom.className = 'border border-gray-200 rounded-lg overflow-hidden my-3 whitespace-normal';
      dom.setAttribute('data-callout', '');
      dom.setAttribute('data-callout-style', node.attrs.calloutStyle);

      const editorHtml = this.options.templates?.editor_html;
      dom.innerHTML = editorHtml || '<div data-content-dom></div>';

      fetchAndCache(node.attrs);

      const contentDOM = dom.querySelector('[data-content-dom]');

      // Set the correct active pill for the current style on initial render
      dom.querySelectorAll('[data-style-key]').forEach(pill => {
        const isActive = pill.dataset.styleKey === node.attrs.calloutStyle;
        const add = (isActive ? pill.dataset.activeClass : pill.dataset.inactiveClass).split(' ');
        const remove = (isActive ? pill.dataset.inactiveClass : pill.dataset.activeClass).split(' ');
        pill.classList.remove(...remove);
        pill.classList.add(...add);
      });

      dom.querySelector('[data-remove]')?.addEventListener('mousedown', (e) => {
        e.preventDefault();
        const pos = typeof getPos === 'function' ? getPos() : null;
        if (pos !== null) {
          const current = editor.state.doc.nodeAt(pos);
          if (current) {
            editor.chain().focus().deleteRange({ from: pos, to: pos + current.nodeSize }).run();
          }
        }
      });

      dom.querySelectorAll('[data-style-key]').forEach(pill => {
        pill.addEventListener('mousedown', (e) => {
          e.preventDefault();
          const pos = typeof getPos === 'function' ? getPos() : null;
          if (pos !== null) {
            editor.chain().focus().setNodeSelection(pos).updateAttributes('callout', { calloutStyle: pill.dataset.styleKey }).run();
          }
        });
      });

      return {
        dom,
        contentDOM,
        update(updatedNode) {
          if (updatedNode.type.name !== 'callout') return false;

          dom.setAttribute('data-callout-style', updatedNode.attrs.calloutStyle);

          dom.querySelectorAll('[data-style-key]').forEach(pill => {
            const isActive = pill.dataset.styleKey === updatedNode.attrs.calloutStyle;
            const add = (isActive ? pill.dataset.activeClass : pill.dataset.inactiveClass).split(' ');
            const remove = (isActive ? pill.dataset.inactiveClass : pill.dataset.activeClass).split(' ');
            pill.classList.remove(...remove);
            pill.classList.add(...add);
          });

          // Fetch rendered HTML for the new style if not already cached
          fetchAndCache(updatedNode.attrs);

          return true;
        },
      };
    };
  },

  addCommands() {
    return {
      insertCallout: (attrs = {}) => ({ chain }) => {
        return chain()
          .insertContent({
            type: this.name,
            attrs: { calloutStyle: 'info', ...attrs },
          })
          .run();
      },
    };
  },
});

export const calloutSlashItem = {
  title: 'Callout',
  description: 'Highlight important information',
  icon: 'i',
  group: 'Blocks',
  keywords: ['callout', 'note', 'info', 'tip', 'warning', 'alert'],
  command: ({ editor, range }) => {
    editor.chain().focus().deleteRange(range).setNode('callout', { calloutStyle: 'info' }).run();
  },
};
